import React, { useEffect, useState } from 'react';
import { Card, CardTitle, Metric, Button, Tag } from '../components/UI';
import RouteMap from '../components/RouteMap';
import NegotiationChat from '../components/NegotiationChat';
import HosPanel from '../components/HosPanel';
import { useAuth } from '../context/AuthContext';
import { useUpgradeGate, ProBadge } from '../components/UpgradeGate';
import {
  getShipmentDetail,
  runOptimization,
  rankCarriers,
  bookShipment,
  updateBookingStatus,
  rateConfirmationUrl,
} from '../api';
import {
  Loader2,
  ArrowLeft,
  Zap,
  Share2,
  Truck,
  CheckCircle2,
  Clock,
  PackageCheck,
  XCircle,
  MessageSquare,
  Download,
  Lock,
} from 'lucide-react';

const BOOKING_FLOW = ['pending', 'confirmed', 'in_transit', 'delivered'];
const BOOKING_LABEL = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_transit: 'In transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

function BookingStepper({ status }) {
  const activeIdx = BOOKING_FLOW.indexOf(status);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {BOOKING_FLOW.map((s, i) => {
        const done = activeIdx >= i;
        const current = activeIdx === i;
        return (
          <React.Fragment key={s}>
            <div
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
              style={{
                background: done ? 'var(--lf-green-bg)' : '#f1f5f9',
                color: done ? 'var(--lf-green)' : '#64748b',
                border: current ? '1px solid var(--lf-green)' : '1px solid transparent',
              }}
            >
              {done ? <CheckCircle2 size={12} /> : <Clock size={12} />}
              {BOOKING_LABEL[s]}
            </div>
            {i < BOOKING_FLOW.length - 1 && (
              <div style={{ width: 18, height: 2, background: done ? 'var(--lf-green)' : '#e2e8f0' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ShipmentDetail({ shipmentId, onBack }) {
  const { user } = useAuth();
  const isPro = !!(user?.effective_pro || user?.is_pro);
  const { guard, Modal: UpgradeModal } = useUpgradeGate();
  const [data, setData] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [error, setError] = useState('');
  const [bookingBusy, setBookingBusy] = useState(false);
  const [confirmCarrier, setConfirmCarrier] = useState(null);
  const [shareCopied, setShareCopied] = useState(null); // optimization id
  const [chatCarrier, setChatCarrier] = useState(null);

  const load = async () => {
    try {
      const [d, ranked] = await Promise.all([
        getShipmentDetail(shipmentId),
        rankCarriers(shipmentId).catch(() => []),
      ]);
      setData(d);
      setSelectedOpt(d.optimizations[0] || null);
      setCarriers(ranked);
    } catch (e) {
      setError('Failed to load shipment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const handleReoptimize = async () => {
    if (!data) return;
    setRunning(true);
    try {
      await runOptimization({
        origin: data.shipment.origin,
        destination: data.shipment.destination,
        stops: data.shipment.stops || [],
        weight: data.shipment.weight,
        equipment: data.shipment.equipment,
        pickup: data.shipment.pickup,
        delivery: data.shipment.delivery,
        constraints: data.shipment.constraints,
        shipment_id: data.shipment.id,
      });
      await load();
    } finally {
      setRunning(false);
    }
  };

  const handleShare = async (optId) => {
    const url = `${window.location.origin}/share/plan/${optId}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(optId);
      setTimeout(() => setShareCopied(null), 2500);
    } catch (e) {
      window.prompt('Copy this link:', url);
    }
  };

  const handleDispatch = async () => {
    if (!confirmCarrier) return;
    setBookingBusy(true);
    try {
      await bookShipment(shipmentId, confirmCarrier.id);
      setConfirmCarrier(null);
      await load();
    } finally {
      setBookingBusy(false);
    }
  };

  const handleAdvanceStatus = async (nextStatus) => {
    if (!data || !data.booking) return;
    setBookingBusy(true);
    try {
      await updateBookingStatus(data.booking.id, nextStatus);
      await load();
    } finally {
      setBookingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--lf-muted)' }}>
        <Loader2 className="animate-spin" size={14} /> Loading shipment…
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-sm text-red-600">{error || 'Not found.'}</p>;
  }

  const s = data.shipment;
  const opt = selectedOpt;
  const booking = data.booking;
  const nextIdx =
    booking ? Math.min(BOOKING_FLOW.indexOf(booking.status) + 1, BOOKING_FLOW.length - 1) : -1;
  const nextStatus = nextIdx > BOOKING_FLOW.indexOf(booking?.status) ? BOOKING_FLOW[nextIdx] : null;

  return (
    <div className="space-y-[18px]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium"
          style={{
            color: 'var(--lf-blue)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={15} /> Back to shipments
        </button>
        <Button variant="primary" onClick={handleReoptimize} disabled={running}>
          {running ? (
            <><Loader2 className="animate-spin" size={15} /> Re-running…</>
          ) : (
            <><Zap size={15} /> Re-run optimizer</>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[18px]">
        <Card>
          <div className="flex items-baseline justify-between mb-3">
            <CardTitle>{s.shipment_code}</CardTitle>
            <Tag variant={s.status === 'Dispatched' || s.status === 'Optimized' || s.status === 'In transit' || s.status === 'Delivered' ? 'success' : 'warning'}>
              {s.status}
            </Tag>
          </div>
          <div className="text-base font-semibold mb-4">
            {[s.origin, ...(s.stops || []), s.destination].join(' → ')}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Metric label="Equipment" value={s.equipment} />
            <Metric label="Weight" value={`${s.weight.toLocaleString()} lb`} />
            <Metric label="ETA" value={s.eta || '—'} />
            <Metric
              label="Projected cost"
              value={s.projected_cost ? `$${s.projected_cost.toLocaleString()}` : '—'}
            />
          </div>
          <div
            className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-2"
            style={{ color: 'var(--lf-muted)' }}
          >
            <div>
              <b className="font-semibold" style={{ color: '#111827' }}>Pickup:</b> {s.pickup}
            </div>
            <div>
              <b className="font-semibold" style={{ color: '#111827' }}>Delivery:</b> {s.delivery}
            </div>
            {(s.stops || []).length > 0 && (
              <div className="sm:col-span-2">
                <b className="font-semibold" style={{ color: '#111827' }}>Stops:</b>{' '}
                {(s.stops || []).join(' → ')}
              </div>
            )}
            {s.constraints && (
              <div className="sm:col-span-2">
                <b className="font-semibold" style={{ color: '#111827' }}>Constraints:</b>{' '}
                {s.constraints}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardTitle>Route map</CardTitle>
          <RouteMap
            origin={s.origin}
            destination={s.destination}
            stops={s.stops || []}
            height={300}
          />
        </Card>
      </div>

      {/* Carrier dispatch */}
      <Card>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <CardTitle>Carrier dispatch</CardTitle>
          {booking && (
            <div className="flex items-center gap-2 flex-wrap">
              <BookingStepper status={booking.status} />
              {nextStatus && booking.status !== 'delivered' && (
                <Button
                  variant="secondary"
                  onClick={() => handleAdvanceStatus(nextStatus)}
                  disabled={bookingBusy}
                >
                  {bookingBusy ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <PackageCheck size={14} />
                  )}
                  Mark {BOOKING_LABEL[nextStatus]}
                </Button>
              )}
              {booking.status !== 'cancelled' && booking.status !== 'delivered' && (
                <Button
                  variant="secondary"
                  onClick={() => handleAdvanceStatus('cancelled')}
                  disabled={bookingBusy}
                >
                  <XCircle size={14} /> Cancel
                </Button>
              )}
            </div>
          )}
        </div>
        {booking ? (
          <div
            className="p-3.5 rounded-lg mb-3"
            style={{ background: '#f8faff', border: '1px solid var(--lf-line)' }}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Truck size={16} style={{ color: 'var(--lf-blue)' }} />
                <b className="text-sm">{booking.carrier_name}</b>
                <span className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                  rate {booking.rate}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isPro ? (
                  <a
                    href={rateConfirmationUrl(booking.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg no-underline"
                    style={{ background: '#eef2f7', color: '#344054' }}
                  >
                    <Download size={13} /> Rate confirmation PDF
                  </a>
                ) : (
                  <button
                    onClick={() => guard('Download rate confirmation PDF')}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg"
                    style={{
                      background: '#eef2f7',
                      color: '#344054',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Lock size={11} /> PDF <ProBadge />
                  </button>
                )}
                <span className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                  Booked {new Date(booking.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        <div className="text-xs mb-2" style={{ color: 'var(--lf-muted)' }}>
          Ranked for this shipment’s equipment, weight, and lanes.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {carriers.map((c) => {
            const isAssigned = booking && booking.carrier_id === c.id;
            return (
              <div
                key={c.id}
                className="p-3.5 rounded-lg"
                style={{
                  border: isAssigned ? '2px solid var(--lf-blue)' : '1px solid var(--lf-line)',
                  background: '#fff',
                }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <b className="text-sm">{c.name}</b>
                  <b className="text-sm" style={{ color: 'var(--lf-green)' }}>{c.match}%</b>
                </div>
                <div
                  className="h-1.5 rounded-full overflow-hidden mb-2"
                  style={{ background: '#e9edf4' }}
                >
                  <div
                    className="h-full"
                    style={{ background: 'var(--lf-green)', width: `${c.match}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs mb-2" style={{ color: 'var(--lf-muted)' }}>
                  <span>rate {c.rate}</span>
                  <span>on-time {c.on_time}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {(c.reasons || []).slice(0, 2).map((r, i) => (
                    <span
                      key={i}
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      style={{ background: '#eef2ff', color: '#3730a3' }}
                    >
                      {r}
                    </span>
                  ))}
                </div>
                <Button
                  variant={isAssigned ? 'secondary' : 'primary'}
                  onClick={() => setConfirmCarrier(c)}
                  disabled={isAssigned || (booking && booking.status !== 'cancelled')}
                  className="w-full"
                >
                  <Truck size={14} />
                  {isAssigned ? 'Dispatched' : booking ? 'Change…' : 'Dispatch'}
                </Button>
                <button
                  onClick={() => {
                    if (guard('Negotiate rates with carriers')) setChatCarrier(c);
                  }}
                  className="w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-1.5 rounded-lg"
                  style={{
                    background: 'transparent',
                    color: 'var(--lf-blue)',
                    border: '1px solid var(--lf-line)',
                    cursor: 'pointer',
                  }}
                >
                  {isPro ? <MessageSquare size={12} /> : <Lock size={11} />}
                  Negotiate {!isPro && <ProBadge className="ml-1" />}
                </button>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Optimization history */}
      <Card>
        <CardTitle>Optimization history ({data.optimizations.length})</CardTitle>
        {data.optimizations.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--lf-muted)' }}>
            No optimizations saved for this shipment yet. Click “Re-run optimizer” above.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
            <div className="space-y-2">
              {data.optimizations.map((o) => {
                const active = opt && opt.id === o.id;
                const dt = new Date(o.created_at);
                return (
                  <button
                    key={o.id}
                    onClick={() => setSelectedOpt(o)}
                    className="w-full text-left p-3 rounded-lg"
                    style={{
                      border: active ? '2px solid var(--lf-blue)' : '1px solid var(--lf-line)',
                      background: active ? '#f8faff' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">Score {o.score}/100</span>
                      <span className="text-xs" style={{ color: 'var(--lf-muted)' }}>
                        {dt.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs flex gap-3" style={{ color: 'var(--lf-muted)' }}>
                      <span>${o.cost.toLocaleString()}</span>
                      <span>{o.miles} mi</span>
                      <span>{o.duration}</span>
                      <span>ETA {o.eta}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {opt && (
              <div
                className="p-4 rounded-lg"
                style={{ background: '#f8faff', border: '1px solid var(--lf-line)' }}
              >
                <div className="flex justify-between items-center mb-2">
                  <b className="text-sm">Plan details</b>
                  <b className="text-sm" style={{ color: 'var(--lf-green)' }}>
                    {opt.score} / 100
                  </b>
                </div>
                <div
                  className="h-2 rounded-full overflow-hidden mb-3"
                  style={{ background: '#e9edf4' }}
                >
                  <div
                    className="lf-score-bar block h-full"
                    style={{ background: 'var(--lf-green)', width: `${opt.score}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <Metric label="Cost" value={`$${opt.cost.toLocaleString()}`} />
                  <Metric label="Fuel" value={`$${opt.fuel.toLocaleString()}`} />
                  <Metric label="Miles" value={opt.miles.toLocaleString()} />
                  <Metric label="Duration" value={opt.duration} />
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ color: '#475467' }}>
                  <b>Why:</b> {opt.explanation}
                </p>
                <HosPanel hos={opt} />
                <div className="mt-3">
                  <Button variant="secondary" onClick={() => handleShare(opt.id)}>
                    <Share2 size={14} />
                    {shareCopied === opt.id ? 'Link copied!' : 'Share this plan'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Dispatch confirmation */}
      {confirmCarrier && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(15,23,42,.45)' }}
          onClick={() => !bookingBusy && setConfirmCarrier(null)}
        >
          <div
            className="bg-white rounded-xl p-5 w-full max-w-md"
            style={{ boxShadow: '0 20px 50px rgba(15,23,42,.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-2">Dispatch shipment?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--lf-muted)' }}>
              Assign <b style={{ color: '#111827' }}>{confirmCarrier.name}</b> to{' '}
              <b style={{ color: '#111827' }}>{s.shipment_code}</b> at rate{' '}
              <b style={{ color: '#111827' }}>{confirmCarrier.rate}</b>.
            </p>
            <div
              className="text-xs mb-4 p-2.5 rounded"
              style={{ background: '#f8faff', color: '#475467' }}
            >
              Match score {confirmCarrier.match}% • on-time {confirmCarrier.on_time}
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmCarrier(null)}
                disabled={bookingBusy}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDispatch} disabled={bookingBusy}>
                {bookingBusy ? (
                  <><Loader2 className="animate-spin" size={14} /> Dispatching…</>
                ) : (
                  <><Truck size={14} /> Confirm dispatch</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {chatCarrier && (
        <NegotiationChat
          shipmentId={shipmentId}
          carrier={chatCarrier}
          onClose={() => setChatCarrier(null)}
        />
      )}
      {UpgradeModal}
    </div>
  );
}
